import { useState } from 'react';

import { CAVEAT_MANUAL, type CounterpartyKind, type SelectableParty, type SkippedDocument } from '@clm/shared-types';

import { PartyPicker } from '../../components/PartyPicker';
import { VendorPicker } from '../../components/VendorPicker';
import { leadingSuggestion, matchPercent } from '../party-match';
import { TYPE_PENDING_RESTRICTED_COPY } from './IngestExecute';
import { rowKey, type RowOverride } from './planner-store';
import { classifySowShape, MAX_SOW_NUMBER_LENGTH, SHAPE_REFUSAL, SOW_SHAPE_LABEL } from './sow-shape';

/**
 * "Correct and include" — CLM-71 (D-71-0).
 *
 * Gerry, 11 Sep 2026: *"if CLM is not able to pick the SOW number I should be
 * able to add it manually — give me that option in the plan ingest phase."* A
 * `to_skip` row whose reason is correctable (a SOW number the planner could not
 * derive, or a party it could not resolve) opens this editor inline. The admin
 * types the number and/or picks the client; "Include this document" records the
 * correction in the planner store (D-71-2 — the plan stays a dry run) and the row
 * moves to the ingest card under "Corrected by hand", **never pre-ticked**.
 *
 * Two rules from the contract are enforced here so the wire never carries what
 * the server would refuse anyway:
 *
 *  - **FR-14** — the party is a selection through `PartyPicker` (CLM-68), never
 *    typed text. `onChange` hands back the party row the API offered, and only
 *    its `party_key` is stored.
 *  - **IR-01** — the typed number must look like one the source system could
 *    hold. {@link classifySowShape} mirrors `@clm/sharepoint`'s
 *    `classifySowCandidate` (this app takes no dependency on that package): the
 *    four unusable shapes disable Include and name themselves in a `role="alert"`;
 *    `sow_infix` / `plausible` are accepted with the weak-shape tag (mirror in
 *    `./sow-shape.ts`). The server re-validates with the real rules and
 *    upper-cases; the mirror only keeps a hopeless value from being posted.
 *
 * For `sow_number_ambiguous` rows the plan's `candidates` render as quick picks
 * that fill the field — the server still checks the picked number and, if it is
 * held by a different party, answers `party_mismatch` on the receipt.
 *
 * A `sow_number_*` row already has a party from its folder; the skipped row does
 * not carry it, so the picker starts empty and is optional there — leave it
 * empty and the server keeps the folder's party. A `party_key_not_derivable`
 * row needs the client picked.
 *
 * **CLM-118 — nobody picks a type (D-110-3, B9).** The row's type is the
 * planner's `derived_type` (an `MSA` / `NDA` / `SOW` word in the name or folder),
 * shown read-only. The reviewer type override and the offered-type gate are
 * retired (D-76-4, D-76-5, D-76-7; R-118-18). An MSA / NDA carries no SOW number
 * (D-76-6) and is includable with no further input when its party is known.
 *
 * **CLM-122 (R-122-7) — a correction files by the plan's own rule**, read from the
 * row's `contract_type_name_evidence`:
 *
 *  - a party skip whose name contradicts itself is **Type pending** — no SOW-number
 *    field (none applies), includable once the client is picked;
 *  - a party skip whose name gives no type keeps the optional number field: typed,
 *    the row files as a SOW (the server's step 2b, a type the reviewer's number
 *    gave, never a trusted file-name type); left blank, it is **Type pending** and
 *    the AI decides the type after filing — unless the picked client is
 *    processing-restricted, when the editor says the row is never sent to the AI
 *    and an administrator types it (rule 4). The server decides for the picked
 *    client — a bare number in the name that resolves against that client's SOWs
 *    files a SOW instead, and the receipt says so;
 *  - every other row keeps its rules, including a word-less SOW-number skip whose
 *    name says SOW by its number or holds an ambiguous one: Include needs a number.
 *
 * **CLM-126 — the nearest-match fallback.** A `party_key_not_derivable` or
 * `party_ambiguous` row may carry up to 3 trigram-scored `nearest_matches`
 * (never on another skip reason). They seed the picker's `initialParties`
 * (so a pick renders its name without a search) and, when the leading one
 * clears the suggest floor and the near-tie guard ({@link leadingSuggestion}),
 * a one-click "Use … (NN% match)" button beside it — the same quick-pick
 * pattern the SOW candidates above already use. Clicking it is still an
 * ordinary `PartyPicker` selection (FR-14): nothing here writes a party key
 * that did not come from the API. Accepting is recorded (`suggestion_accepted`)
 * so the server can credit the right audit actor; picking anything else
 * through the picker itself clears it — the human's final choice wins
 * (D-88-4's precedence, extended, R-126-12).
 */

/* -------------------------------------------------------------------------- */
/* Editor                                                                      */
/* -------------------------------------------------------------------------- */

export interface CorrectionEditorProps {
  readonly row: SkippedDocument;
  /** The correction already recorded for this row, when editing one. */
  readonly current: RowOverride | null;
  readonly disabled?: boolean;
  readonly onApply: (override: RowOverride) => void;
  readonly onCancel: () => void;
}

export function CorrectionEditor({
  row,
  current,
  disabled = false,
  onApply,
  onCancel,
}: CorrectionEditorProps) {
  const key = rowKey(row);
  const [sowNumber, setSowNumber] = useState(current?.sow_number_override ?? '');
  const [party, setParty] = useState<SelectableParty | null>(null);
  const [partyKey, setPartyKey] = useState(current?.party_key_override ?? '');
  /** CLM-126 (R-126-12) — set only by the quick-pick below; any other change to `partyKey` clears it. */
  const [suggestionAccepted, setSuggestionAccepted] = useState(current?.suggestion_accepted ?? null);
  /**
   * CLM-78 (B6) — Client or Vendor toggles the picker. A vendor reaches the plan
   * only through `party_key_override` (CLM-71); the kind itself is never sent —
   * the party's own `party_type` is what the server files (K5).
   */
  const [kind, setKind] = useState<CounterpartyKind>('client');

  const trimmed = sowNumber.trim();
  const shape = classifySowShape(trimmed);
  /* CLM-122 (R-122-7) — only a party skip can carry a name with no trusted type; the client is what it lacks. */
  const partySkip = row.reason === 'party_key_not_derivable' || row.reason === 'party_ambiguous';
  const conflictingName = partySkip && row.contract_type_name_evidence === 'conflicting';
  const untypedName = partySkip && row.derived_type === null && row.contract_type_name_evidence === 'untyped';
  /* The number field is the type's evidence when the name gave none: a typed number is what says "SOW". */
  const showSow = !conflictingName && (row.derived_type === null || row.derived_type === 'SOW');
  const sowGiven = showSow && trimmed !== '';
  const sowInvalid = sowGiven && !shape.usable;
  /* CLM-122 — the correction files the row as Type pending: a name that contradicts itself, or no type and no number. */
  const typePendingReason = conflictingName ? 'conflicting_names' : untypedName && !sowGiven ? 'no_name_type' : undefined;
  /* What the server files the row as: Type pending's OTHER, the planner's type, or SOW from the typed number; `null` — none yet. */
  const type = typePendingReason !== undefined ? 'OTHER' : (row.derived_type ?? (sowGiven ? 'SOW' : null));
  const isSow = type === 'SOW';
  const partyGiven = partyKey !== '';
  /* Rule 4 — the picked party's flag (the server re-derives it); a restricted Type pending row is never read by the AI. */
  const restricted = (party?.processing_restricted ?? current?.display.processing_restricted) === true;
  const partyRequired = row.reason === 'party_key_not_derivable' || typePendingReason !== undefined;
  /* A SOW the planner could not number needs one typed; with a party correction the server may resolve it. */
  const sowRequired = isSow && !partyRequired;
  const canInclude =
    !disabled &&
    type !== null &&
    (!partyRequired || partyGiven) &&
    (isSow ? !sowInvalid && (!sowRequired || sowGiven) && (sowGiven || partyGiven) : true);


  function apply() {
    /* The button is offered only for a row with an eTag; the guard keeps the type honest. */
    if (!canInclude || row.etag === null) return;
    const upper = trimmed.toUpperCase();
    onApply({
      etag: row.etag,
      ...(sowGiven ? { sow_number_override: upper } : {}),
      ...(partyGiven ? { party_key_override: partyKey } : {}),
      /* CLM-126 (R-126-12) — only when the accepted suggestion is still the
       * party actually being sent; `setParty`/`setPartyKey` and the radio
       * toggle above already clear `suggestionAccepted` on any other change,
       * so this guard is belt-and-braces, not load-bearing. */
      ...(suggestionAccepted !== null && suggestionAccepted.party_key === partyKey
        ? { suggestion_accepted: suggestionAccepted }
        : {}),
      display: {
        party_label: party === null ? (partyGiven ? current?.display.party_label ?? partyKey : null) : `${party.display_name} (${party.party_key})`,
        processing_restricted: party?.processing_restricted ?? current?.display.processing_restricted ?? false,
        sow_number_shape: sowGiven ? shape.shape : null,
        contract_type: type,
        ...(typePendingReason === undefined ? {} : { type_pending_reason: typePendingReason }),
      },
    });
  }

  return (
    <tr className="correct-editor" id={`correct-${key}`} data-row={key}>
      <td colSpan={3}>
        <div className="frow">
          <div className="field" data-testid={`type-${key}`}>
            <span className="mut">Contract type</span>
            {typePendingReason === 'conflicting_names' ? (
              <p>
                <strong>Type pending</strong> <span className="mut">— the name names more than one type</span>
              </p>
            ) : typePendingReason === 'no_name_type' ? (
              <p>
                <strong>Type pending</strong>{' '}
                <span className="mut">
                  {restricted
                    ? `— ${TYPE_PENDING_RESTRICTED_COPY}`
                    : '— the AI reads it after filing and decides the type; the receipt shows what was filed'}
                </span>
              </p>
            ) : row.derived_type !== null ? (
              <p>
                <strong>{row.derived_type}</strong> <span className="mut">— from the file or folder name</span>
              </p>
            ) : type !== null ? (
              <p>
                <strong>{type}</strong> <span className="mut">— from the SOW number you typed</span>
              </p>
            ) : (
              /* CLM-122 — only a name with no type word but a SOW-shaped or ambiguous number reaches here (D-71-0). */
              <p className="ferr" id={`type-${key}-alert`} role="alert">
                No type word in the name, and its number is a SOW’s to confirm — type the SOW number to include it.
              </p>
            )}
          </div>

          {showSow ? (
            <div className="field">
              <label htmlFor={`sow-${key}`}>SOW number</label>
              <input
                id={`sow-${key}`}
                type="text"
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                maxLength={MAX_SOW_NUMBER_LENGTH + 8}
                placeholder="e.g. AGESOW25264OV"
                value={sowNumber}
                disabled={disabled}
                aria-invalid={sowInvalid ? true : undefined}
                aria-describedby={sowInvalid ? `sow-${key}-alert` : undefined}
                onChange={(event) => setSowNumber(event.target.value)}
                onBlur={() => setSowNumber((value) => value.trim().toUpperCase())}
              />
              {row.candidates.length > 0 ? (
                <span className="fl quickpicks" aria-label="Candidates the planner found">
                  {row.candidates.map((candidate) => (
                    <button
                      key={candidate}
                      type="button"
                      className="btn sm qt"
                      disabled={disabled}
                      onClick={() => setSowNumber(candidate)}
                    >
                      Use {candidate}
                    </button>
                  ))}
                </span>
              ) : null}
              {sowInvalid ? (
                <p className="ferr" id={`sow-${key}-alert`} role="alert">
                  Not usable as a SOW number — {SOW_SHAPE_LABEL[shape.shape]}: {SHAPE_REFUSAL[shape.shape]}.
                </p>
              ) : sowGiven && shape.shape !== 'dominant_shape' ? (
                <p className="mut">
                  <span className="tag ai">weak shape: {SOW_SHAPE_LABEL[shape.shape]}</span> accepted — reconciliation
                  (FR-15) checks it afterwards.
                </p>
              ) : null}
            </div>
          ) : conflictingName ? (
            <div className="field">
              <span className="mut">No SOW number applies — the row is filed as Type pending without one.</span>
            </div>
          ) : (
            <div className="field">
              <span className="mut">
                No SOW number — an {type} carries none (D-76-6).
              </span>
            </div>
          )}

          <div className="field grow">
            <fieldset className="kind-toggle" disabled={disabled}>
              <legend className="mut">Counterparty</legend>
              <label>
                <input
                  type="radio"
                  name={`kind-${key}`}
                  value="client"
                  aria-label="Counterparty is a client"
                  checked={kind === 'client'}
                  onChange={() => {
                    setKind('client');
                    setParty(null);
                    setPartyKey('');
                    setSuggestionAccepted(null);
                  }}
                />{' '}
                Client agreement
              </label>
              <label>
                <input
                  type="radio"
                  name={`kind-${key}`}
                  value="vendor"
                  aria-label="Counterparty is a vendor"
                  checked={kind === 'vendor'}
                  onChange={() => {
                    setKind('vendor');
                    setParty(null);
                    setPartyKey('');
                    setSuggestionAccepted(null);
                  }}
                />{' '}
                Vendor agreement
              </label>
            </fieldset>
            <label htmlFor={`party-${key}`}>
              {kind === 'vendor'
                ? 'Vendor'
                : `Client${partyRequired ? '' : ' (optional — blank keeps the folder’s client)'}`}
            </label>
            {kind === 'vendor' ? (
              <VendorPicker
                id={`party-${key}`}
                value={partyKey}
                disabled={disabled}
                initialParties={row.nearest_matches}
                onChange={(picked) => {
                  setParty(picked);
                  setPartyKey(picked?.party_key ?? '');
                  setSuggestionAccepted(null);
                }}
              />
            ) : (
              <PartyPicker
                id={`party-${key}`}
                value={partyKey}
                disabled={disabled}
                initialParties={row.nearest_matches}
                onChange={(picked) => {
                  setParty(picked);
                  setPartyKey(picked?.party_key ?? '');
                  setSuggestionAccepted(null);
                }}
              />
            )}
            {/* CLM-126 (R-126-13 (2)) — the one-click suggestion, exactly the SOW
             * quick-picks' pattern above: a free pick through the picker itself,
             * never a separate code path. Rendered only when the leading
             * candidate clears the suggest floor and the near-tie guard. */}
            {(() => {
              const leading = leadingSuggestion(row.nearest_matches);
              if (leading === null) return null;
              return (
                <span className="fl quickpicks">
                  <button
                    type="button"
                    className="btn sm pri"
                    disabled={disabled}
                    onClick={() => {
                      setParty(leading);
                      setPartyKey(leading.party_key);
                      setSuggestionAccepted({
                        party_key: leading.party_key,
                        score: leading.suggestion_score ?? 0,
                      });
                    }}
                  >
                    Use {leading.display_name} ({matchPercent(leading.suggestion_score ?? 0)})
                  </button>
                </span>
              );
            })()}
            {restricted ? (
              <p className="mut">
                <span
                  className="tag ai"
                  title="Processing-restricted: stored, converted in-tenant, indexed and searchable — never sent to an LLM. Facts are entered by hand."
                >
                  manual entry — no LLM
                </span>
              </p>
            ) : null}
          </div>
        </div>

        <div className="actions">
          <button type="button" className="btn sm pri" disabled={!canInclude} onClick={apply}>
            Include this document
          </button>
          <button type="button" className="btn sm qt" disabled={disabled} onClick={onCancel}>
            Cancel
          </button>
          <span className="mut" title={CAVEAT_MANUAL}>
            The server checks the number’s shape, the client’s scope and stored contracts again on Execute; the
            row is never pre-ticked.
          </span>
        </div>
      </td>
    </tr>
  );
}
